import { useMemo } from 'react';
import { useBrandIdentity } from './useBrandIdentity';

/** One named brand color, e.g. `{ name: 'primary', value: '#3A5A78' }`. */
export interface BrandColor {
  name: string;
  value: string;
}

export interface BrandColorsResult {
  colors: BrandColor[];
  isLoading: boolean;
  error: string | null;
}

/** The roles the design agent knows, first and in this order. */
const PREFERRED_ORDER = ['primary', 'secondary', 'accent'];

const NO_COLORS: BrandColor[] = [];

function toColors(record: Record<string, string>): BrandColor[] {
  return Object.entries(record)
    .filter(([, value]) => value.trim().length > 0)
    .map(([name, value]) => ({ name, value: value.trim() }))
    .sort((left, right) => {
      const leftRank = PREFERRED_ORDER.indexOf(left.name);
      const rightRank = PREFERRED_ORDER.indexOf(right.name);
      if (leftRank !== rightRank) {
        return (
          (leftRank === -1 ? PREFERRED_ORDER.length : leftRank) -
          (rightRank === -1 ? PREFERRED_ORDER.length : rightRank)
        );
      }
      return left.name.localeCompare(right.name);
    });
}

/**
 * The business's brand colors, read out of the brand-identity singleton.
 *
 * The layer editor offers these and nothing else: brand colors are the
 * knowledge base's to set, not the editor's to invent (SRS — colors, fonts and
 * layer order are not user-editable at review time).
 */
export function useBrandColors(businessId: string | null): BrandColorsResult {
  const { identity, isLoading, error } = useBrandIdentity(businessId);
  const colors = useMemo(
    () => (identity === null ? NO_COLORS : toColors(identity.colors)),
    [identity],
  );

  return { colors, isLoading, error };
}
